import { productsService } from './products';

export interface ProductWithTax {
  productId: string;
  productName: string;
  productCategory: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  taxAmount: number;
  taxRate?: number;
  taxClassId?: string;
}

export interface TaxCalculationResult {
  subtotal: number;
  taxAmount: number;
  total: number;
  taxBreakdown: Array<{
    taxClassId: string;
    taxClassName: string;
    taxRate: number;
    taxableAmount: number;
    taxAmount: number;
  }>;
}

export class TaxCalculationService {
  private static instance: TaxCalculationService;
  private productCache: Map<string, any> = new Map();

  private constructor() {}

  public static getInstance(): TaxCalculationService {
    if (!TaxCalculationService.instance) {
      TaxCalculationService.instance = new TaxCalculationService();
    }
    return TaxCalculationService.instance;
  }

  private productCacheKey(productId: string, clubRouteKey?: string | null): string {
    return clubRouteKey ? `${clubRouteKey}:${productId}` : productId;
  }

  /**
   * Calculate tax for a single product
   */
  async calculateProductTax(
    productId: string,
    unitPrice: number,
    quantity: number = 1,
    clubRouteKey?: string | null,
  ): Promise<{
    totalPrice: number;
    taxAmount: number;
    taxRate: number;
    taxClassId?: string;
    taxClassName?: string;
  }> {
    try {
      const product = await this.getProductWithTaxClass(productId, clubRouteKey);

      if (!product) {
        console.warn(`Product ${productId} not found, using 0% tax`);
        return {
          totalPrice: unitPrice * quantity,
          taxAmount: 0,
          taxRate: 0,
        };
      }

      const totalPrice = unitPrice * quantity;
      const taxRate = product.taxClass?.rate || 0;
      const taxAmount = totalPrice * (taxRate / 100);

      return {
        totalPrice,
        taxAmount: Math.round(taxAmount * 100) / 100,
        taxRate,
        taxClassId: product.taxClassId,
        taxClassName: product.taxClass?.name,
      };
    } catch (error) {
      console.error('Error calculating product tax:', error);
      return {
        totalPrice: unitPrice * quantity,
        taxAmount: 0,
        taxRate: 0,
      };
    }
  }

  /**
   * Calculate tax for multiple products
   */
  async calculateProductsTax(
    products: Array<{
      productId: string;
      unitPrice: number;
      quantity?: number;
    }>,
    clubRouteKey?: string | null,
  ): Promise<TaxCalculationResult> {
    const taxBreakdown = new Map<
      string,
      {
        taxClassId: string;
        taxClassName: string;
        taxRate: number;
        taxableAmount: number;
        taxAmount: number;
      }
    >();

    let subtotal = 0;
    let totalTaxAmount = 0;

    for (const product of products) {
      const result = await this.calculateProductTax(
        product.productId,
        product.unitPrice,
        product.quantity || 1,
        clubRouteKey,
      );

      subtotal += result.totalPrice;
      totalTaxAmount += result.taxAmount;

      if (result.taxClassId && result.taxAmount > 0) {
        const existing = taxBreakdown.get(result.taxClassId);
        if (existing) {
          existing.taxableAmount += result.totalPrice;
          existing.taxAmount += result.taxAmount;
        } else {
          taxBreakdown.set(result.taxClassId, {
            taxClassId: result.taxClassId,
            taxClassName: result.taxClassName || 'Tax',
            taxRate: result.taxRate,
            taxableAmount: result.totalPrice,
            taxAmount: result.taxAmount,
          });
        }
      }
    }

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(totalTaxAmount * 100) / 100,
      total: Math.round((subtotal + totalTaxAmount) * 100) / 100,
      taxBreakdown: Array.from(taxBreakdown.values()),
    };
  }

  /**
   * Convert products to ProductWithTax format
   */
  async convertToProductsWithTax(
    products: Array<{
      productId: string;
      productName: string;
      productCategory: string;
      quantity: number;
      unitPrice: number;
    }>,
    clubRouteKey?: string | null,
  ): Promise<ProductWithTax[]> {
    const productsWithTax: ProductWithTax[] = [];

    for (const product of products) {
      const taxResult = await this.calculateProductTax(
        product.productId,
        product.unitPrice,
        product.quantity,
        clubRouteKey,
      );

      productsWithTax.push({
        productId: product.productId,
        productName: product.productName,
        productCategory: product.productCategory,
        quantity: product.quantity,
        unitPrice: product.unitPrice,
        totalPrice: taxResult.totalPrice,
        taxAmount: taxResult.taxAmount,
        taxRate: taxResult.taxRate,
        taxClassId: taxResult.taxClassId,
      });
    }

    return productsWithTax;
  }

  /**
   * Get product with tax class information (with caching)
   */
  private async getProductWithTaxClass(
    productId: string,
    clubRouteKey?: string | null,
  ): Promise<any> {
    const cacheKey = this.productCacheKey(productId, clubRouteKey);
    if (this.productCache.has(cacheKey)) {
      return this.productCache.get(cacheKey);
    }

    try {
      const product = await productsService.getById(productId, clubRouteKey);
      this.productCache.set(cacheKey, product);
      return product;
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);
      return null;
    }
  }

  /**
   * Clear caches (useful for testing or when data changes)
   */
  clearCache(): void {
    this.productCache.clear();
  }

  /**
   * Get tax summary for display
   */
  getTaxSummary(taxBreakdown: TaxCalculationResult['taxBreakdown']): string {
    if (taxBreakdown.length === 0) {
      return 'No tax';
    }

    if (taxBreakdown.length === 1) {
      const tax = taxBreakdown[0];
      return `${tax.taxClassName} (${tax.taxRate}%)`;
    }

    return `${taxBreakdown.length} tax classes`;
  }
}

// Export singleton instance
export const taxCalculationService = TaxCalculationService.getInstance();
